import { isAbsoluteURL, isDev, isRelativeURL } from '@lucid/utils';
import qs from 'query-string';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';

import useSiteMetadata from './use-site-metadata';
import type { Lucid } from '../types/types';

type ImageMutationOptions = {
  /** The fit of the thumbnail while always preserving the aspect ratio, can be any of the following options:
   * cover — Covers both width/height by cropping/clipping to fit
   * contain — Contain within both width/height using "letterboxing" as needed
   * inside — Resize to be as large as possible, ensuring dimensions are less than or equal to the requested width and height
   * outside — Resize to be as small as possible, ensuring dimensions are greater than or equal to the requested width and height
   */
  fit?: 'cover' | 'contain' | 'inside' | 'outside';
  /** The width of the thumbnail in pixels */
  width?: number;
  /** The height of the thumbnail in pixels */
  height?: number;
  /** The optional quality of the thumbnail (1 to 100) */
  quality?: number;
  /** Disable image up-scaling */
  withoutEnlargement?: boolean;
  /** What file format to return the thumbnail in. One of auto, jpg, png, webp, tiff.
   * auto — Will try to format it in webp or avif if the browser supports it, otherwise it will fallback to jpg.
   */
  format?: 'auto' | 'jpg' | 'png' | 'webp';
  /** any operation found here can be included https://sharp.pixelplumbing.com/api-operation
   * an example: [["blur", 45],["tint", "rgb(255, 0, 0)"],["expand", { "right": 200, "bottom": 150 }]]
   * found from https://docs.directus.io/reference/files.html#advanced
   */
  transforms?: string;
  /** The product, that overwrites the site metadata product value */
  product?: Lucid.Product.Sites;
  [key: string]: boolean | number | string | undefined;
};

const WithImage = (media?: Lucid.Directus.Generated.Directus_Files, options?: ImageMutationOptions): string => {
  const { product } = useSiteMetadata();

  if (isEmpty(media) || isUndefined(media)) return '';

  const filePath = media?.filename_disk;

  if (isEmpty(filePath) || isUndefined(filePath)) return '';

  const { product: overwriteProduct, ...currentOptions } = options ?? {};
  const currentProduct = overwriteProduct || product;

  const versionKey = media.uploaded_on ? Date.parse(media.uploaded_on) : '';

  const currentQueryParams = qs.stringify(
    {
      ...currentOptions,
      v: versionKey,
    },
    {
      skipNull: true,
      skipEmptyString: true,
    },
  );

  // check if absolute or /static path is passed in and return that value unchanged
  // This will catch external data sources mimicking the proper data structure but that we don't want to change ex: marketplace intergration pages on lucid.co
  if ((isAbsoluteURL(filePath) && !filePath.includes('/assets')) || filePath.startsWith('/static'))
    return `${filePath}${currentQueryParams ? `?${currentQueryParams}` : ''}`;

  // Return cashy urls unchanged
  if (isRelativeURL(filePath) && filePath.includes('cdn-cashy-static-assets')) return filePath;

  // check if the filename_disk starts with `/assets`
  if (filePath.startsWith('/assets') || filePath.includes('/assets')) {
    const currentFilePath = qs.parseUrl(filePath);
    const currentFileQueryParams = qs.stringify(
      { ...currentOptions, ...currentFilePath.query, v: versionKey },
      {
        skipNull: true,
        skipEmptyString: true,
      },
    );

    return `https://corporate-assets.lucid.co/${currentProduct.replace('blog', '')}${currentFilePath.url
      .replace('https://lucidchart.directus.app', '')
      .replace('https://lucidco.directus.app', '')
      .replace('https://lucidedu.directus.app', '')
      .replace('https://lucidscale.directus.app', '')
      .replace('https://lucidspark.directus.app', '')
      .replace('/assets', '')
      .replace('*PROD', '')}${currentFileQueryParams ? `?${currentFileQueryParams}` : ''}`;
  }

  if (isDev() && !filePath.includes('*PROD')) return `http://${currentProduct}.localhost/assets/${filePath}`;

  return `https://corporate-assets.lucid.co/${currentProduct.replace('blog', '')}/${filePath.replace('*PROD', '')}${currentQueryParams ? `?${currentQueryParams}` : ''}`;
};

export default WithImage;
